import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import * as LiveLessonActions from "../store/live-lesson.actions";
import * as fromLesson from "../store/lesson.selectors";
import { Observable, take } from "rxjs";
import { distinctUntilChanged, filter, map, pairwise, shareReplay } from "rxjs/operators";
import { LiveLessonId } from "@pcp/core/interfaces";
import {
    lessonActionsOpenScreen,
    pauseLessonActions,
    startLessonActions,
    stopLessonActions,
} from "../store/lesson.actions";


@Injectable({
    providedIn: 'root',
})
export class LessonService {

    private readonly store = inject(Store);

    private lessonState$ = this.store.select(fromLesson.selectLessonState);
    readonly isLessonStarted$ = this.store.select(fromLesson.isLessonStarted);
    readonly isLessonPaused$ = this.store.select(fromLesson.isLessonPaused);

    readonly isLoadingLessonCode$ = this.store.select(fromLesson.isLoadingLessoncode);
    readonly lessonButtonState$ = this.store.select(fromLesson.selectLessonButtonState);
    readonly lessonStatus = this.store.select(fromLesson.selectLessonState);
    readonly studentRegistrationCode = this.store.select(fromLesson.selectStudentRegistrationCode);
    readonly isShowingRegistrationCodeOnBeamer = this.store.select(fromLesson.selectIsShowingStudentRegistrationCode);
    readonly lessonStartedAt$ = this.store.select(fromLesson.selectStartedAt);
    readonly liveLessonId$: Observable<LiveLessonId|undefined> = this.store.select(fromLesson.selectLiveLessonId);

    startLesson() {
        this.store.dispatch(startLessonActions.startLesson());
    }

    async stopLesson() {
        this.lessonState$.pipe(take(1)).subscribe(state => {
            if (state.started === false) {
                throw new Error('Cannot stop lesson as it is not started yet.');
            }

            this.store.dispatch(stopLessonActions.stopLesson());
        });
    }

    openAdditionalBeamerScreen(): void {
        this.lessonState$.pipe(take(1)).subscribe(state => {
            if (!state.started || (!state.activeLesson?.lessonId && !state.liveLessonId)) {
                throw new Error('Cannot open an additional beamer screen because there is no actively running lesson.');
            }

            this.store.dispatch(lessonActionsOpenScreen.openAdditionalBeamerScreen());
        });
    }

    pauseLesson() {
        this.store.dispatch(pauseLessonActions.pauseLesson());
    }

    resumeLesson() {
        this.store.dispatch(pauseLessonActions.resumeLesson());
    }

    get onPauseStateChanged(): Observable<boolean> {
        return this.lessonStatus.pipe(
            map((lessonState) => lessonState.paused),
            distinctUntilChanged(),
            pairwise(),
            filter(
                ([lastPauseState, nextPauseState]) =>
                    lastPauseState !== nextPauseState
            ),
            map(([lastPauseState, nextPauseState]) => nextPauseState),
            shareReplay(1)
        );
    }

    startLiveLesson(liveLessonId: LiveLessonId) {
        this.store.dispatch(LiveLessonActions.startLiveLesson({ liveLessonId }));
    }

    registerNewLiveLesson() {
        this.store.dispatch(LiveLessonActions.registerLiveLesson());
    }

    fetchLiveLessonList() {
        this.store.dispatch(LiveLessonActions.fetchLiveLessonList());
    }
}
